import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { signOut } from './services/auth';

// ── Public consumer pages — eagerly imported (small, must paint fast) ──
import LandingPage from './pages/LandingPage';
import OrderPage from './pages/OrderPage';
import TrackOrderPage from './pages/TrackOrderPage';
import LogisticsPortalPage from './pages/LogisticsPortalPage';
import LoginPage from './pages/LoginPage';

// ── Admin pages — lazy-loaded; consumer visitors never download them ──
const DashboardPage         = React.lazy(() => import('./pages/DashboardPage'));
const SalesPage             = React.lazy(() => import('./pages/SalesPage'));
const ClientsPage           = React.lazy(() => import('./pages/ClientsPage'));
const ClientDetailPage      = React.lazy(() => import('./pages/ClientDetailPage'));
const ExpensesPage          = React.lazy(() => import('./pages/ExpensesPage'));
const InventoryPage         = React.lazy(() => import('./pages/InventoryPage'));
const InventoryTrackerPage  = React.lazy(() => import('./pages/InventoryTrackerPage'));
const CallAssistantPage     = React.lazy(() => import('./pages/CallAssistantPage'));
const ReportsPage           = React.lazy(() => import('./pages/ReportsPage'));
const SuppliersPage         = React.lazy(() => import('./pages/SuppliersPage'));
const PaymentsPage          = React.lazy(() => import('./pages/PaymentsPage'));
const AnalyticsPage         = React.lazy(() => import('./pages/AnalyticsPage'));
const StoreManagerPage      = React.lazy(() => import('./pages/StoreManagerPage'));
const SuperAdminHQPage      = React.lazy(() => import('./pages/SuperAdminHQPage'));
const AuditLogPage          = React.lazy(() => import('./pages/AuditLogPage'));
const OrdersManagementPage  = React.lazy(() => import('./pages/OrdersManagementPage'));
const DeliveryEarningsDashboard = React.lazy(() => import('./pages/DeliveryEarningsDashboard'));
const SalInsightsPage        = React.lazy(() => import('./pages/SalInsightsPage'));
const MembershipsPage        = React.lazy(() => import('./pages/MembershipsPage'));
const SupplyPlansPage        = React.lazy(() => import('./pages/SupplyPlansPage'));
const SupplyRequestPage      = React.lazy(() => import('./pages/SupplyRequestPage'));
const LogisticsDashboard    = React.lazy(() => import('./pages/LogisticsDashboard'));
const SalChatPage           = React.lazy(() => import('./pages/SalChatPage'));
const StaffRequestsPage     = React.lazy(() => import('./pages/StaffRequestsPage'));
const IssueReportsPage      = React.lazy(() => import('./pages/IssueReportsPage'));
const COODashboard          = React.lazy(() => import('./pages/COODashboard'));
const CFODashboard          = React.lazy(() => import('./pages/CFODashboard'));
const CTODashboard          = React.lazy(() => import('./pages/CTODashboard'));
const ProductPhotoManager   = React.lazy(() => import('./components/Admin/ProductPhotoManager'));
const PriceManager          = React.lazy(() => import('./components/Admin/PriceManager'));

// Layout
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/UI/LoadingSpinner';
// FloatingSalWidget pulls Gemini + Firestore; only loaded for admin users
const FloatingSalWidget = React.lazy(() => import('./components/Sal/FloatingSalWidget'));

// Roles allowed inside the admin panel
const ADMIN_ROLES = ['super_admin', 'admin', 'store_manager', 'regional_manager'];
const isAdminRole = (role?: string) => !!role && ADMIN_ROLES.includes(role);

// Wraps authenticated admin routes in the sidebar Layout
const AdminLayout: React.FC = () => (
  <Layout>
    <Suspense fallback={
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    }>
      <Outlet />
    </Suspense>
  </Layout>
);

// Handles the /admin staff portal:
// - not logged in → show sign-in-only LoginPage
// - logged in as admin role → redirect to /dashboard
// - logged in as non-admin → show warning + sign-out button
const StaffLogin: React.FC = () => {
  const { user, loading } = useAuth();
  const [signingOut, setSigningOut] = React.useState(false);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <LoadingSpinner size="large" />
    </div>
  );
  if (!user) return <LoginPage signInOnly />;
  if (isAdminRole(user.role)) return <Navigate to="/dashboard" replace />;

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Wrong Account</h2>
        <p className="text-gray-500 text-sm mb-2">
          You are signed in as <strong>{user.name}</strong>
        </p>
        <p className="text-gray-400 text-xs mb-6">
          This account ({user.role}) does not have admin access. Sign out and log in with an admin account.
        </p>
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors"
        >
          {signingOut ? 'Signing out...' : 'Sign Out & Switch Account'}
        </button>
        <a href="/" className="block mt-3 text-sm text-gray-400 hover:text-gray-600">Back to ordering page</a>
      </div>
    </div>
  );
};

// Requires login AND an admin-level role; non-admins bounce to consumer homepage
const RequireAdminAuth: React.FC = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/admin" replace />;
  if (!isAdminRole(user.role)) return <Navigate to="/" replace />;
  return <Outlet />;
};

const App: React.FC = () => {
  const { loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* ===== PUBLIC CONSUMER ROUTES — always standalone, no auth ===== */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/order" element={<OrderPage />} />
        <Route path="/track" element={<TrackOrderPage />} />
        <Route path="/supply" element={<SupplyRequestPage />} />
        <Route path="/logistics-portal" element={<LogisticsPortalPage />} />

        {/* ===== STAFF LOGIN — hidden, accessible via /admin ===== */}
        <Route path="/admin" element={<StaffLogin />} />
        {/* legacy /login alias */}
        <Route path="/login" element={<Navigate to="/admin" replace />} />

        {/* ===== PROTECTED ADMIN ROUTES — admin roles only ===== */}
        <Route element={<RequireAdminAuth />}>
          <Route element={<AdminLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/sales" element={<SalesPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/inventory-tracker" element={<InventoryTrackerPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/calls" element={<CallAssistantPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/hq-dashboard" element={<SuperAdminHQPage />} />
            <Route path="/store-manager" element={<StoreManagerPage />} />
            <Route path="/orders" element={<OrdersManagementPage />} />
            <Route path="/delivery-earnings" element={<DeliveryEarningsDashboard />} />
            <Route path="/sal-insights" element={<SalInsightsPage />} />
            <Route path="/memberships" element={<MembershipsPage />} />
            <Route path="/supply-plans" element={<SupplyPlansPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/logistics" element={<LogisticsDashboard />} />
            <Route path="/sal-chat" element={<SalChatPage />} />
            <Route path="/staff-requests" element={<StaffRequestsPage />} />
            <Route path="/issue-reports" element={<IssueReportsPage />} />
            <Route path="/product-photos" element={<ProductPhotoManager />} />
            <Route path="/price-settings" element={<PriceManager />} />
            <Route path="/coo-dashboard" element={<COODashboard />} />
            <Route path="/cfo-dashboard" element={<CFODashboard />} />
            <Route path="/cto-dashboard" element={<CTODashboard />} />
          </Route>
        </Route>

        {/* Catch-all → consumer homepage */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Floating Sal AI Widget - admin-only (loads on demand) */}
      {isAdminRole(user?.role) && (
        <Suspense fallback={null}>
          <FloatingSalWidget />
        </Suspense>
      )}
    </>
  );
};

export default App;

